import React, { useState, useEffect } from 'react';
import { Card, Spinner, Button, Modal, Form, Badge } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import EditNoteIcon from '@mui/icons-material/EditNote';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// `manageMode` (used by the admin panel) shows hidden posts too; the public
// page always shows only visible ones.
const News = ({ manageMode = false }) => {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [form, setForm] = useState({
        title: '',
        content: '',
        visible: true
    });
    const [error, setError] = useState(null);
    const { isAuthenticated } = useAuth();

    const [editMode, setEditMode] = useState(false);
    const [editId, setEditId] = useState(null);

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deletePost, setDeletePost] = useState(null);
    const [deleteError, setDeleteError] = useState(null);

    const fetchNews = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${BACKEND_URL}/news`);
            setPosts(response.data);
        } catch {
            setPosts([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchNews();
    }, []);

    const handleShowModal = () => {
        setForm({ title: '', content: '', visible: true });
        setError(null);
        setEditMode(false);
        setEditId(null);
        setShowModal(true);
    };

    const handleShowEditModal = (post) => {
        setForm({
            title: post.title,
            content: post.content,
            visible: post.visible !== undefined ? post.visible : true
        });
        setError(null);
        setEditMode(true);
        setEditId(post.id);
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setError(null);
        setEditMode(false);
        setEditId(null);
    };

    const handleFormChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        try {
            if (editMode && editId) {
                await axios.post(`${BACKEND_URL}/news/edit`, { id: editId, ...form }, { withCredentials: true });
            } else {
                await axios.post(`${BACKEND_URL}/news/add`, form, { withCredentials: true });
            }
            setShowModal(false);
            setEditMode(false);
            setEditId(null);
            fetchNews();
        } catch (err) {
            setError('Błąd podczas zapisywania wpisu.');
        }
    };

    const handleShowDeleteModal = (post) => {
        setDeletePost(post);
        setDeleteError(null);
        setShowDeleteModal(true);
    };

    const handleCloseDeleteModal = () => {
        setShowDeleteModal(false);
        setDeletePost(null);
        setDeleteError(null);
    };

    const handleDelete = async () => {
        setDeleteError(null);
        try {
            await axios.delete(`${BACKEND_URL}/news/delete/${deletePost.id}`, { withCredentials: true });
            setShowDeleteModal(false);
            setDeletePost(null);
            fetchNews();
        } catch (err) {
            setDeleteError('Błąd podczas usuwania wpisu.');
        }
    };

    const handleToggleVisible = async (post) => {
        setError(null);
        try {
            await axios.post(
                `${BACKEND_URL}/news/edit`,
                {
                    id: post.id,
                    title: post.title,
                    content: post.content,
                    visible: !post.visible,
                },
                { withCredentials: true }
            );
            fetchNews();
        } catch {
            setError('Błąd podczas zmiany widoczności wpisu.');
        }
    };

    // Public visitors and the public homepage never see hidden posts; the admin
    // panel (manageMode) shows them so they can be toggled back on.
    const displayedPosts = manageMode ? posts : posts.filter((p) => p.visible !== false);

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 200 }}>
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Ładowanie...</span>
                </Spinner>
            </div>
        );
    }

    return (
        <div className="cards-container">
            {isAuthenticated && (
                <div className="mb-3">
                    <Button variant="primary" onClick={handleShowModal}>
                        Dodaj wpis
                    </Button>
                </div>
            )}

            {displayedPosts.length === 0 && <div>Brak aktualności.</div>}

            {displayedPosts.map(post => (
                <Card
                    key={post.id}
                    className="mb-4"
                    style={{ opacity: post.visible === false ? 0.65 : 1 }}
                >
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-start">
                            <div>
                                <Card.Title>
                                    {post.title}
                                    {post.visible === false && (
                                        <Badge bg="secondary" className="ms-2 align-middle">
                                            Ukryty
                                        </Badge>
                                    )}
                                </Card.Title>
                            </div>
                            {isAuthenticated && (
                                <div className="d-flex align-items-center">
                                    <Button
                                        variant="link"
                                        className="p-0 ms-2"
                                        style={{ color: '#6c757d', display: 'flex', alignItems: 'center' }}
                                        onClick={() => handleToggleVisible(post)}
                                        title={post.visible === false ? 'Pokaż wpis' : 'Ukryj wpis'}
                                    >
                                        {post.visible === false ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                    </Button>
                                    <Button
                                        variant="link"
                                        className="p-0 ms-2"
                                        style={{ color: '#0d6efd', display: 'flex', alignItems: 'center' }}
                                        onClick={() => handleShowEditModal(post)}
                                        title="Edytuj wpis"
                                    >
                                        <EditNoteIcon />
                                    </Button>
                                    <Button
                                        variant="link"
                                        className="p-0 ms-2"
                                        style={{ color: '#dc3545', display: 'flex', alignItems: 'center' }}
                                        onClick={() => handleShowDeleteModal(post)}
                                        title="Usuń wpis"
                                    >
                                        <DeleteForeverIcon />
                                    </Button>
                                </div>
                            )}
                        </div>
                        <div style={{ whiteSpace: 'pre-wrap', wordWrap: 'normal' }}>{post.content}</div>
                        <div className="text-muted" style={{ fontSize: '0.9em' }}>
                            Opublikowano: {new Date(post.published).toLocaleString()}
                            {post.edited && (
                                <span> | Edytowano: {new Date(post.edited).toLocaleString()}</span>
                            )}
                        </div>
                    </Card.Body>
                </Card>
            ))}

            <Modal show={showModal} onHide={handleCloseModal}>
                <Modal.Header closeButton>
                    <Modal.Title>{editMode ? 'Edytuj wpis' : 'Dodaj wpis'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {error && <div className="text-danger mb-2">{error}</div>}
                    <Form onSubmit={handleSubmit}>
                        <Form.Group className="mb-3">
                            <Form.Label>Tytuł</Form.Label>
                            <Form.Control
                                type="text"
                                name="title"
                                value={form.title}
                                onChange={handleFormChange}
                                required
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Treść</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={5}
                                name="content"
                                value={form.content}
                                onChange={handleFormChange}
                                required
                            />
                        </Form.Group>
                        <Form.Group className="mb-3" controlId="visibleCheckbox">
                            <Form.Check
                                type="checkbox"
                                label="Widoczny"
                                name="visible"
                                checked={form.visible}
                                onChange={handleFormChange}
                            />
                        </Form.Group>
                        <div className="d-flex justify-content-end">
                            <Button variant="primary" type="submit">
                                {editMode ? 'Zapisz zmiany' : 'Dodaj'}
                            </Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>

            <Modal show={showDeleteModal} onHide={handleCloseDeleteModal}>
                <Modal.Header closeButton>
                    <Modal.Title>Potwierdź usunięcie wpisu</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {deleteError && <div className="text-danger mb-2">{deleteError}</div>}
                    {deletePost && (
                        <>
                            <h5>{deletePost.title}</h5>
                            <p>{deletePost.content}</p>
                            <div className="text-danger mb-3">Czy na pewno chcesz usunąć ten wpis?</div>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseDeleteModal}>
                        Anuluj
                    </Button>
                    <Button variant="danger" onClick={handleDelete}>
                        Usuń
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default News;